<template>
  <div class="freebets-round pt-0 text-black animation-slide">
    <div class="content-box-style flex flex-col gap-3">
      <Filters
        :options="options"
        :showActionButton="true"
        :search="search"
        :resetFilter="resetFilter"
        :resetPagination="resetPagination"
      />

      <div
        v-if="showRestoreButton"
       
      >
        <span class="flex items-center gap-2 text-blue-700">
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8"></path>
            <path d="M21 3v5h-5"></path>
            <path
              d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"
            ></path>
            <path d="M3 21v-5h5"></path>
          </svg>
          <small class="font-medium">
            If you see the icon, you can restore the freebet to the player,
            resetting the usage counter.
          </small>
        </span>
      </div>

      <div
        v-if="isLoading"
        class="flex-1 flex items-center justify-center py-20"
      >
        <Loader />
      </div>

      <div
        v-else-if="tableData.length === 0"
        class="flex-1 flex flex-col items-center justify-center py-10 text-gray-400"
      >
        <i class="pi pi-search text-4xl mb-3 opacity-80"></i>
        <span class="font-medium">No players found.</span>
        <small>Try adjusting your filters or search criteria.</small>
      </div>

      <DataTable
        v-else
        :value="tableData"
        :lazy="true"
        :paginator="true"
        :rows="selectedLimit"
        :totalRecords="totalElements"
        :first="(currentPage - 1) * selectedLimit"
        @page="onPage"
        @sort="onSort"
        :sortField="sortField"
        :sortOrder="sortOrder === 'desc' ? -1 : 1"
        dataKey="player_id"
        removableSort
        stripedRows
        scrollable
        scrollHeight="flex"
        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
        :rowsPerPageOptions="[15, 25, 50, 100]"
        class="table-container-custom shadow-sm rounded-2xl overflow-hidden border border-1 mb-3"
      >
        <Column field="player_id" header="Player ID" sortable>
          <template #body="{ data }">
            <span class="font-bold text-gray-800">{{ data.player_id }}</span>
          </template>
        </Column>

        <Column field="quantity" header="Quantity" sortable />

        <Column field="amount" header="Amount" sortable>
          <template #body="{ data }">
            <span class="text-indigo-600 font-semibold">{{ data.amount }}</span>
          </template>
        </Column>

        <Column field="counter" header="Counter" sortable>
          <template #body="{ data }">
            <span
              class="px-2 py-0.5 bg-gray-100 rounded text-gray-600 text-xs font-mono"
            >
              {{ data.counter }}
            </span>
          </template>
        </Column>

        <Column field="active" header="Active" sortable>
          <template #body="{ data }">
            <i
              :class="
                data.active
                  ? 'pi pi-check-circle text-emerald-500'
                  : 'pi pi-times-circle text-red-400'
              "
            ></i>
          </template>
        </Column>

        <Column field="created_at" header="Start Time" sortable>
          <template #body="{ data }">
            <span class="text-gray-500 whitespace-nowrap text-xs">
              {{ formatDate(data.created_at) }}
            </span>
          </template>
        </Column>

        <Column header="Actions" alignFrozen="right" frozen>
          <template #body="{ data }">
            <div class="flex items-center gap-1">
              <Button
                v-if="showRestoreButton"
                icon="pi pi-refresh"
                class="p-button-rounded p-button-text p-button-sm text-yellow-600 hover:bg-yellow-50"
                @click="restoreFreebets(data)"
              />
              <Button
                icon="pi pi-trash"
                class="p-button-rounded p-button-text p-button-sm text-red-500 hover:bg-red-50"
                @click="removePlayerFromFreebet(data)"
              />
            </div>
          </template>
        </Column>
      </DataTable>
    </div>
  </div>
</template>

<script>
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import Button from "primevue/button";
import Filters from "../table/Filters.vue";
import Loader from "../Loader.vue";
import { getFetch, postFetch, deleteRequest } from "@/api/axiosClient.js";
import Swal from "sweetalert2";

export default {
  name: "FreebetPlayersActive",
  components: { Filters, DataTable, Column, Button, Loader },
  props: {
    freebet_id: { type: String, required: true },
    skinFilt: { type: Number, required: true },
  },
  data() {
    return {
      // Lazy states
      currentPage: 1,
      selectedLimit: 15,
      totalElements: 0,
      sortField: "created_at",
      sortOrder: "desc",

      isLoading: false,
      tableData: [],
      skin_id: 0,
      player_id: "",
      showRestoreButton: true,

      options: [
        {
          label: "Player ID",
          type: "text",
          placeholder: "Search for Player ID",
          property: "player_id",
        },
        {
          label: "Freebet ID",
          type: "text",
          placeholder: "Freebet ID",
          property: "freebet_id",
          disabled: true,
        },
      ],
    };
  },
  methods: {
    formatDate(val) {
      return val ? new Date(val).toLocaleString("en-GB") : "-";
    },
    // Gestione Paginazione PrimeVue
    onPage(event) {
      this.currentPage = event.page + 1;
      this.selectedLimit = event.rows;
      this.search();
    },
    // Gestione Sort PrimeVue
    onSort(event) {
      this.sortField = event.sortField;
      this.sortOrder = event.sortOrder === 1 ? "asc" : "desc";
      this.search();
    },
    async resetFilter() {
      this.currentPage = 1;
      this.player_id = "";
      this.sortField = "created_at";
      this.sortOrder = "desc";
      await this.search();
    },
    async resetPagination() {
      this.currentPage = 1;
    },
    async search() {
      this.isLoading = true;
      try {
        const params = {
          skin_id: this.skin_id,
          page: this.currentPage,
          limit: this.selectedLimit,
          sortField: this.sortField,
          sortOrder: this.sortOrder,
        };

        if (this.player_id) params.player_id = this.player_id.trim();

        const data = await getFetch(
          `freebets/players/${this.freebet_id}`,
          params,
        );
        this.tableData = data.data;
        this.totalElements = data.totalItems;
      } catch (error) {
        console.error("Fetch error:", error);
      } finally {
        this.isLoading = false;
      }
    },
    // Azioni SweetAlert (Restore e Delete rimangono logica core)
    async restoreFreebets(row) {
      const result = await Swal.fire({
        title: this.$t("freebetsDetail.are_you_sure") ?? "Are you sure?",
        html: `Restore freebet for player <b class="text-warning">${row.player_id}</b>?`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes",
      });

      if (result.isConfirmed) {
        try {
          await postFetch(`/freebets/restoreplayer`, {
            freebet_id: this.freebet_id,
            player_id: row.player_id,
          });
          Swal.fire("Restored!", "Player was restored.", "success");
          this.search(); // Refresh invece di reload totale
        } catch (error) {
          Swal.fire("Error", "Something went wrong.", "error");
        }
      }
    },
    async removePlayerFromFreebet(row) {
      const result = await Swal.fire({
        title: "Are you sure?",
        text: `Remove player ${row.player_id} from this campaign?`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, remove it!",
      });

      if (result.isConfirmed) {
        try {
          this.isLoading = true;
          await deleteRequest(`freebets/player/${this.freebet_id}`, {
            player_id: row.player_id,
          });
          Swal.fire("Deleted", "The player has been removed.", "success");
          this.search();
        } catch (error) {
          Swal.fire("Error", "Failed to remove player.", "error");
        } finally {
          this.isLoading = false;
        }
      }
    },
  },
  mounted() {
    this.skin_id = this.skinFilt;
    this.search();
  },
};
</script>

<style scoped>
/* Stile per allineare l'icona restore nel testo di spiegazione */
.inline-flex {
  display: inline-flex;
  vertical-align: middle;
}
</style>
